import type { MarketplacePurchase } from "../vend/expense/types";
import { buildUrl, defaultFetchHeaders, fetchJson } from "../utils";

export type SubscriptionState =
  | "trial"
  | "grace"
  | "active"
  | "past_due"
  | "canceled"
  | "exempt";

export type ChargeType = "subscription_cycle" | "machine_activation" | "marketplace" | "direct_deposit_monthly" | "direct_deposit_fees";

export type DirectDepositLine = { recipientId: string; recipient: string; locations: Record<string, string>; amountCents: number; description?: string | null; stripeTransactionId?: string | null; commissionPaymentId?: string | null };
export type DirectDepositMonthlyCharge = ChargeHistoryEntryBase & ({ chargeType: "direct_deposit_monthly" } | { chargeType: "direct_deposit_fees" }) & {
  billingCycleStart: string;
  billingCycleEnd: string;
  directDepositLines: DirectDepositLine[];
};
export type DirectDepositPlan = {
  enabled: boolean;
  month: string; monthlyFeeCents: number; activeRecipients: number; amountCents: number;
  exempt: boolean; exemptReason: string | null; exemptUntil: string | null;
  recipients: { id: string; label: string; locations: Record<string, string>; amountCents: number; waived: boolean }[];
};

export type BillingProfile = {
  trialStartedAt: string;
  trialEndsAt: string;
  trialActive: boolean;
  billingExempt?: boolean;
  billingExemptReason?: string | null;
  billingExemptUntil?: string | null;
  lastBilledAt?: string | null;
};

export type ChargeHistoryEntryBase = {
  id: string;
  amountCents: number;
  description: string;
  status: string;
  chargeType: ChargeType;
  paymentMethodLabel?: string | null;
  createdAt?: string | null;
  chargedAt?: string | null;
  linkedExpenseId?: string | null;
  machineNameSnapshots: Record<string, string>;
};

export type SubscriptionCycleChargeHistoryEntry = ChargeHistoryEntryBase & {
  chargeType: "subscription_cycle";
  billingCycleStart: string;
  billingCycleEnd: string;
  includedMachineIds: string[];
  additionalMachineIds: string[];
  baseFeeCents: number;
  includedMachineLimit: number;
  additionalMachineFeeCents: number;
};

export type MachineActivationChargeHistoryEntry = ChargeHistoryEntryBase & {
  chargeType: "machine_activation";
  machineId: string;
  billingCycleStart: string;
  billingCycleEnd: string;
};

export type MarketplaceChargeHistoryEntry = ChargeHistoryEntryBase & {
  chargeType: "marketplace";
  currency: string;
  marketplacePurchase: MarketplacePurchase | null;
};

export type OrganizationChargeDetail = {
  charge: ChargeHistoryEntry;
  relatedCharges: ChargeHistoryEntry[];
};

export type ChargeHistoryEntry =
  | DirectDepositMonthlyCharge
  | SubscriptionCycleChargeHistoryEntry
  | MachineActivationChargeHistoryEntry
  | MarketplaceChargeHistoryEntry;

export type SubscriptionSummary = {
  state: SubscriptionState;
  billingProfile: BillingProfile;
  charges: ChargeHistoryEntry[];
};

export type BillingStatus = {
  trialActive: boolean;
  trialStartedAt: string;
  trialEndsAt: string;
  cycleStart: string;
  cycleEnd: string;
  lastBilledAt?: string | null;
  nextBillingAt?: string | null;
  hasPaymentMethod: boolean;
  subscriptionPaymentRequired?: boolean;
  subscriptionState: SubscriptionState;
  lastChargeTotalCents: number;
  cycleChargeTotalCents: number;
  expectedNextChargeCents?: number;
  pricing?: { freeMachineLimit: number; baseFeeCents: number; includedMachineLimit: number; additionalMachineFeeCents: number };
  includedMachineIds: string[];
  additionalMachineIds: string[];
};

export type ChargeNowResult = {
  chargeStatus: "succeeded" | "failed" | "pending";
  updatedCharges: number;
  subscriptionState?: SubscriptionState;
};

export type ChargeStatus = "succeeded" | "failed" | "pending" | "trial" | "waived";

export type CancelPreview = {
  machinesToDeactivate: number;
  tasksToCancel: number;
};

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/user/organization/subscription`;

const MISSING_SUBSCRIPTION_MESSAGE =
  "Subscription is not initialized for this organization. Please initialize subscription and billing records first.";

const mapSubscriptionError = (
  error: unknown,
  fallback = "Request failed"
): Error => {
  const message =
    error instanceof Error ? error.message : String(error || fallback);
  const lowered = message.toLowerCase();
  if (
    lowered.includes("subscription matching query does not exist") ||
    lowered.includes("billingprofile matching query does not exist")
  ) {
    return new Error(MISSING_SUBSCRIPTION_MESSAGE);
  }
  return new Error(message || fallback);
};

const normalizeSubscriptionState = (value: unknown): SubscriptionState => {
  const raw = String(value || "").trim().toLowerCase();
  const normalized = raw.includes(".") ? raw.split(".").pop() || raw : raw;
  if (normalized === "trial") return "trial";
  if (normalized === "grace") return "grace";
  if (normalized === "active") return "active";
  if (normalized === "past_due") return "past_due";
  if (normalized === "canceled") return "canceled";
  if (normalized === "exempt") return "exempt";
  return "trial";
};

const normalizeChargeStatus = (value: unknown): ChargeStatus => {
  const raw = String(value || "").trim().toLowerCase();
  const normalized = raw.includes(".") ? raw.split(".").pop() || raw : raw;
  if (normalized === "succeeded") return "succeeded";
  if (normalized === "failed") return "failed";
  if (normalized === "trial") return "trial";
  if (normalized === "waived") return "waived";
  return "pending";
};

const normalizeChargeType = (value: unknown): ChargeType => {
  const raw = String(value || "").trim().toLowerCase();
  const normalized = raw.includes(".") ? raw.split(".").pop() || raw : raw;
  if (normalized === "machine_activation") return "machine_activation";
  if (normalized === "marketplace") return "marketplace";
  if (normalized === "direct_deposit_fees") return "direct_deposit_fees";
  if (normalized === "direct_deposit_monthly") return "direct_deposit_monthly";
  return "subscription_cycle";
};

const pick = <T = unknown>(obj: any, camel: string, snake: string): T | undefined =>
  (obj?.[camel] ?? obj?.[snake]) as T | undefined;

const normalizeCharge = (charge: any): ChargeHistoryEntry => {
  const chargeType = normalizeChargeType(
    pick<string>(charge, "chargeType", "charge_type")
  );
  const base = {
    id: String(charge?.id || ""),
    amountCents: Number(pick<number>(charge, "amountCents", "amount_cents") ?? 0),
    description: String(charge?.description || "Subscription charge"),
    status: normalizeChargeStatus(charge?.status),
    chargeType,
    paymentMethodLabel:
      pick<string | null>(charge, "paymentMethodLabel", "payment_method_label") ??
      null,
    createdAt: pick<string | null>(charge, "createdAt", "created_at") ?? null,
    chargedAt: pick<string | null>(charge, "chargedAt", "charged_at") ?? null,
    linkedExpenseId:
      pick<string | null>(charge, "linkedExpenseId", "linked_expense_id") ?? null,
    machineNameSnapshots:
      pick<Record<string, string>>(
        charge,
        "machineNameSnapshots",
        "machine_name_snapshots"
      ) || {},
  };

  if (chargeType === "marketplace") {
    return { ...base, chargeType, currency: charge.currency || "USD", marketplacePurchase: charge.marketplacePurchase ?? null };
  }
  if (chargeType === "direct_deposit_monthly" || chargeType === "direct_deposit_fees") {
    return { ...base, chargeType, billingCycleStart: charge.billingCycleStart || "",
      billingCycleEnd: charge.billingCycleEnd || "", directDepositLines: charge.directDepositLines || [] };
  }

  if (chargeType === "machine_activation") {
    return {
      ...base,
      chargeType: "machine_activation" as const,
      machineId: String(pick<string>(charge, "machineId", "machine_id") || ""),
      billingCycleStart:
        pick<string>(charge, "billingCycleStart", "billing_cycle_start") ?? "",
      billingCycleEnd:
        pick<string>(charge, "billingCycleEnd", "billing_cycle_end") ?? "",
    };
  }

  return {
    ...base,
    chargeType: "subscription_cycle" as const,
    billingCycleStart:
      pick<string>(charge, "billingCycleStart", "billing_cycle_start") ?? "",
    billingCycleEnd:
      pick<string>(charge, "billingCycleEnd", "billing_cycle_end") ?? "",
    includedMachineIds: Array.isArray(
      pick<string[]>(charge, "includedMachineIds", "included_machine_ids")
    )
      ? (pick<string[]>(charge, "includedMachineIds", "included_machine_ids") as string[])
      : [],
    additionalMachineIds: Array.isArray(
      pick<string[]>(charge, "additionalMachineIds", "additional_machine_ids")
    )
      ? (pick<string[]>(charge, "additionalMachineIds", "additional_machine_ids") as string[])
      : [],
    baseFeeCents: Number(pick<number>(charge, "baseFeeCents", "base_fee_cents") ?? 0),
    includedMachineLimit: Number(
      pick<number>(charge, "includedMachineLimit", "included_machine_limit") ?? 0
    ),
    additionalMachineFeeCents: Number(
      pick<number>(charge, "additionalMachineFeeCents", "additional_machine_fee_cents") ?? 0
    ),
  };
};

const normalizeSummary = (summary: any): SubscriptionSummary => {
  const billingProfile = summary?.billingProfile ?? summary?.billing_profile ?? {};
  return {
    state: normalizeSubscriptionState(summary?.state),
    billingProfile: {
      trialStartedAt:
        pick<string>(billingProfile, "trialStartedAt", "trial_started_at") || "",
      trialEndsAt:
        pick<string>(billingProfile, "trialEndsAt", "trial_ends_at") || "",
      trialActive: Boolean(
        pick<boolean>(billingProfile, "trialActive", "trial_active")
      ),
      billingExempt: pick<boolean>(billingProfile, "billingExempt", "billing_exempt"),
      billingExemptReason:
        pick<string | null>(
          billingProfile,
          "billingExemptReason",
          "billing_exempt_reason"
        ) ?? null,
      billingExemptUntil:
        pick<string | null>(
          billingProfile,
          "billingExemptUntil",
          "billing_exempt_until"
        ) ?? null,
      lastBilledAt:
        pick<string | null>(billingProfile, "lastBilledAt", "last_billed_at") ??
        null,
    },
    charges: Array.isArray(summary?.charges)
      ? summary.charges.map(normalizeCharge)
      : [],
  };
};

const normalizeStatus = (status: any): BillingStatus => ({
  trialActive: Boolean(pick<boolean>(status, "trialActive", "trial_active")),
  trialStartedAt: pick<string>(status, "trialStartedAt", "trial_started_at") ?? "",
  trialEndsAt: pick<string>(status, "trialEndsAt", "trial_ends_at") ?? "",
  cycleStart: pick<string>(status, "cycleStart", "cycle_start") ?? "",
  cycleEnd: pick<string>(status, "cycleEnd", "cycle_end") ?? "",
  lastBilledAt: pick<string | null>(status, "lastBilledAt", "last_billed_at") ?? null,
  nextBillingAt:
    pick<string | null>(status, "nextBillingAt", "next_billing_at") ?? null,
  hasPaymentMethod: Boolean(
    pick<boolean>(status, "hasPaymentMethod", "has_payment_method")
  ),
  subscriptionState: normalizeSubscriptionState(
    pick<string>(status, "subscriptionState", "subscription_state")
  ),
  lastChargeTotalCents: Number(
    pick<number>(status, "lastChargeTotalCents", "last_charge_total_cents") ?? 0
  ),
  cycleChargeTotalCents: Number(
    pick<number>(status, "cycleChargeTotalCents", "cycle_charge_total_cents") ?? 0
  ),
  subscriptionPaymentRequired: pick<boolean>(status, "subscriptionPaymentRequired", "subscription_payment_required"),
  pricing: pick<BillingStatus["pricing"]>(status, "pricing", "pricing"),
  expectedNextChargeCents: pick<number>(status, "expectedNextChargeCents", "expected_next_charge_cents"),
  includedMachineIds: Array.isArray(
    pick<string[]>(status, "includedMachineIds", "included_machine_ids")
  )
    ? (pick<string[]>(status, "includedMachineIds", "included_machine_ids") as string[])
    : [],
  additionalMachineIds: Array.isArray(
    pick<string[]>(status, "additionalMachineIds", "additional_machine_ids")
  )
    ? (pick<string[]>(status, "additionalMachineIds", "additional_machine_ids") as string[])
    : [],
});

export async function fetchSubscriptionSummary(): Promise<SubscriptionSummary> {
  try {
    const payload = await fetchJson<any>(
      buildUrl(baseUrl, {}),
      {
        method: "GET",
        credentials: "include",
        headers: await defaultFetchHeaders(),
      },
      "Failed to fetch subscription summary"
    );
    return normalizeSummary(payload);
  } catch (error) {
    throw mapSubscriptionError(error, "Failed to fetch subscription summary");
  }
}

export async function fetchSubscriptionStatus(): Promise<BillingStatus> {
  try {
    const payload = await fetchJson<any>(
      buildUrl(`${baseUrl}/status`, {}),
      {
        method: "GET",
        credentials: "include",
        headers: await defaultFetchHeaders(),
      },
      "Failed to fetch subscription status"
    );
    return normalizeStatus(payload);
  } catch (error) {
    throw mapSubscriptionError(error, "Failed to fetch subscription status");
  }
}

export async function chargeSubscriptionNow(): Promise<ChargeNowResult> {
  try {
    const payload = await fetchJson<any>(
      buildUrl(`${baseUrl}/charge_now`, {}),
      {
        method: "POST",
        credentials: "include",
        headers: await defaultFetchHeaders(),
      },
      "Failed to charge subscription"
    );
    const charges = Array.isArray(payload) ? payload.map(normalizeCharge) : [];
    let chargeStatus: ChargeNowResult["chargeStatus"] = "pending";
    if (charges.some((charge) => charge.status === "failed")) {
      chargeStatus = "failed";
    } else if (charges.some((charge) => charge.status === "succeeded")) {
      chargeStatus = "succeeded";
    }
    return {
      chargeStatus,
      updatedCharges: charges.length,
    };
  } catch (error) {
    throw mapSubscriptionError(error, "Failed to charge subscription");
  }
}

export async function cancelSubscription(): Promise<BillingStatus> {
  try {
    const payload = await fetchJson<any>(
      buildUrl(`${baseUrl}/cancel`, {}),
      {
        method: "POST",
        credentials: "include",
        headers: await defaultFetchHeaders(),
      },
      "Failed to cancel subscription"
    );
    return normalizeStatus(payload);
  } catch (error) {
    throw mapSubscriptionError(error, "Failed to cancel subscription");
  }
}

export async function resumeSubscription(): Promise<BillingStatus> {
  try {
    const payload = await fetchJson<any>(
      buildUrl(`${baseUrl}/resume`, {}),
      {
        method: "POST",
        credentials: "include",
        headers: await defaultFetchHeaders(),
      },
      "Failed to resume subscription"
    );
    return normalizeStatus(payload);
  } catch (error) {
    throw mapSubscriptionError(error, "Failed to resume subscription");
  }
}

export async function fetchCancelPreview(): Promise<CancelPreview> {
  try {
    const preview = await fetchJson<any>(
      buildUrl(`${baseUrl}/cancel/preview`, {}),
      {
        method: "GET",
        credentials: "include",
        headers: await defaultFetchHeaders(),
      },
      "Failed to load cancel preview"
    );
    return {
      machinesToDeactivate:
        pick<number>(preview, "machinesToDeactivate", "machines_to_deactivate") ??
        0,
      tasksToCancel: pick<number>(preview, "tasksToCancel", "tasks_to_cancel") ?? 0,
    };
  } catch (error) {
    throw mapSubscriptionError(error, "Failed to load cancel preview");
  }
}


export async function fetchOrganizationCharge(id: string, signal?: AbortSignal): Promise<OrganizationChargeDetail> {
  const payload = await fetchJson<OrganizationChargeDetail>(
    `${process.env.REACT_APP_BACKEND_URL}/user/organization/charges/${encodeURIComponent(id)}`,
    { method: "GET", credentials: "include", headers: await defaultFetchHeaders(), signal },
    "Unable to load charge details.",
  );
  return { charge: normalizeCharge(payload.charge), relatedCharges: payload.relatedCharges.map(normalizeCharge) };
}
